import React from 'react';
import {styled} from '@mui/material/styles';
import capitalize from 'lodash/capitalize';
import PoweredByAmbitionBox from '../PoweredByAB';
import {Box, useMediaQuery} from '@mui/material';
import {addUtmParamsToUrl} from '@jobseeker/lotus/utils';
import Image from '@jobseeker/lotus/components/Image';

const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';

interface Benefit {
  name: string;
  iconUrl?: string;
  count: number;
  totalCount: number;
}

interface BenefitsInfo {
  benefits: Benefit[];
  benefitsUrl: string;
}

interface CompanyBenefitsProps {
  benefitsInfo: BenefitsInfo;
  companyName?: string;
}

const CompanyBenefitsWrapper = styled(Box)(({theme}) => ({
  borderRadius: 12,
  boxShadow: '0 7px 14px 0 rgba(0, 0, 0, 0.01)',
  backgroundColor: '#fff',
  marginTop: 16,
  padding: '18px 22px 15px 16px',
  display: 'flex',
  flexDirection: 'column',
  [theme.breakpoints.down('md')]: {
    paddingLeft: 12,
    paddingRight: 12,
    marginBottom: 24,
  },
}));

const CompanyBenefitsHeading = styled(Box)(() => ({
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'space-between',
  gap: 10,
  fontSize: 16,
  fontWeight: 'bold',
}));

const ViewAllA = styled('a')(({theme}) => ({
  fontFamily: isAppIimjobs ? 'poppins' : 'Nunito Sans',
  fontSize: 14,
  fontWeight: isAppIimjobs ? 500 : 600,
  color: isAppIimjobs ? '#49966E' : theme.palette.primary.main,
  textDecoration: 'none',
  '&:hover': {
    color: '#49966E',
  },
}));

const BenefitsBorder = styled('a')(({theme}) => ({
  marginTop: 20,
  minHeight: '140px',
  borderRadius: 10,
  border: '1px solid #e8e8e8',
  backgroundColor: '#fff',
  display: 'grid',
  gridTemplateColumns: 'repeat(4, 1fr 1px)',
  textDecoration: 'none',
  '@media screen and (max-width: 576px)': {
    gridTemplateColumns: 'repeat(1, 1fr)',
    gridTemplateRows: 'repeat(4, 1fr 0px)',
  },
  [theme.breakpoints.down('md')]: {
    paddingBottom: 16,
  },
}));

const BenefitDiv = styled(Box)(({theme}) => ({
  display: 'flex',
  position: 'relative',
  boxSizing: 'border-box',
  padding: 25,
  flexDirection: 'column',
  cursor: 'pointer',
  alignItems: 'start',
  gap: isAppIimjobs ? '8px' : '20px',
  [theme.breakpoints.down('md')]: {
    paddingLeft: isAppIimjobs ? 16 : 25,
    paddingBottom: isAppIimjobs ? 7 : 25,
    flexDirection: isAppIimjobs ? 'column' : 'row',
    alignItems: 'start',
  },
}));

const SeparatorDiv = styled(Box)(() => ({
  backgroundColor: isAppIimjobs ? 'transparent' : ' #e8e8e8',
  width: '1px',
  marginTop: 18,
  marginBottom: 14,
}));

const BenefitsIcon = styled(Image)(() => ({
  width: 25,
  height: 25,
}));

const BenefitsDivHeading = styled(Box)(() => ({
  fontSize: isAppIimjobs ? 14 : 15,
  fontWeight: 'bold',
  color: 'black',
  marginBottom: 4,
}));

const BenefitsDivSubHeading = styled(Box)(() => ({
  fontSize: 12,
  lineHeight: 'normal',
  color: 'black',
}));

const CompanyBenefits: React.FC<CompanyBenefitsProps> = ({
  benefitsInfo,
  companyName = '',
}) => {
  if (!benefitsInfo || !companyName) return null;
  const {benefits = [], benefitsUrl = ''} = benefitsInfo;

  if (!benefits?.length) return null;

  const getBenefitsDiv = (item: Benefit) => (
    <div key={item.name}>
      <BenefitDiv>
        {item.iconUrl && (
          <BenefitsIcon src={item.iconUrl} alt={`${item.name} icon`} />
        )}
        <div>
          <BenefitsDivHeading>{item.name}</BenefitsDivHeading>
          <BenefitsDivSubHeading>
            {`Reported in ${item.count} out of ${item.totalCount} responses`}
          </BenefitsDivSubHeading>
        </div>
      </BenefitDiv>
    </div>
  );

  const benefitList = benefits
    .filter(item => item.name)
    .slice(0, 4)
    .map(item => getBenefitsDiv(item));

  const benefitPoints: JSX.Element[] = [];
  benefitList.forEach((e, index) => {
    benefitPoints.push(e);
    if (index !== benefitList.length - 1) {
      benefitPoints.push(<SeparatorDiv key={`separator-${index}`} />);
    }
  });

  if (!benefitPoints.length) return null;

  return (
    <CompanyBenefitsWrapper>
      <CompanyBenefitsHeading>
        {capitalize(companyName || '')} Perks & Benefits
        <ViewAllA
          title={`${companyName} Benefits`}
          target="_blank"
          rel="nofollow"
          href={addUtmParamsToUrl(
            benefitsUrl,
            'BENEFITS',
            useMediaQuery('(min-width:900px)')
          )}
          data-testid="view_all_ab_benifits"
        >
          View All
        </ViewAllA>
      </CompanyBenefitsHeading>
      <BenefitsBorder
        title={`${companyName} Benefits`}
        target="_blank"
        rel="nofollow"
        href={addUtmParamsToUrl(
          benefitsUrl,
          'BENEFITS',
          useMediaQuery('(min-width:900px)')
        )}
      >
        {benefitPoints}
      </BenefitsBorder>
      <PoweredByAmbitionBox
        title={`${companyName} Benefits`}
        redirectionUrl={addUtmParamsToUrl(
          benefitsUrl,
          'BENEFITS',
          useMediaQuery('(min-width:900px)')
        )}
      />
    </CompanyBenefitsWrapper>
  );
};

export default CompanyBenefits;
